import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Calendar } from 'react-date-range';
import moment from 'moment';
import { enUS } from 'date-fns/locale';
import { Images } from 'utils';
import { Popper } from '@mui/material';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { ErrMessage } from '../../messages';
import { TypableDateInput } from '../../inputs';

/** `min` / `max` are calendar days: `YYYY-MM-DD`, or a UTC-midnight ISO date loaded from the api. */
const toDay = (d) => moment.utc(d).format('YYYY-MM-DD');
// The calendar compares in local time, so a bound must be local midnight of that day. UTC midnight
// (`new Date('YYYY-MM-DD')`) is the previous evening west of UTC and drops the bound day itself.
const toLocalDate = (d) => moment(toDay(d), 'YYYY-MM-DD').toDate();

export const DateCalendarForm = ({
    label,
    type,
    control,
    name,
    rules,
    max,
    min,
    disabled,
    handleChange,
    readOnly,
    noErrMessage,
    noShowIcons,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;

    const customLocale = {
        ...enUS,
        options: {
            ...enUS.options,
            weekStartsOn: 0,
        },
    };

    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const getNestedError = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    const isValidDate = (dateString) => {
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field, formState: { errors } }) => {
                const error = getNestedError(errors, name);
                return (
                    <>
                        <div style={{ width: '100%' }}>
                            {label && (
                                <p
                                    style={
                                        disabled
                                            ? { color: 'rgba(0, 0, 0, 0.26)' }
                                            : type === 'filterInput'
                                              ? {}
                                              : { color: error?.message ? '#FE7070' : field?.value ? '#347AF0' : '#51566D' }
                                    }
                                    className={type === 'filterInput' ? 'date-selector-label' : 'modal-date-selector-label'}
                                >
                                    {label}
                                </p>
                            )}
                            <button
                                disabled={disabled || readOnly}
                                onClick={handleClick}
                                style={
                                    disabled
                                        ? { borderColor: 'rgba(0, 0, 0, 0.26)' }
                                        : error?.message
                                          ? { borderColor: '#FE7070' }
                                          : type === 'filterInput'
                                            ? {
                                                  width: '165px',
                                                  justifyContent: 'space-between',
                                              }
                                            : {}
                                }
                                className={type === 'filterInput' ? 'date-range-selector' : 'modal-date-range-selector'}
                                aria-describedby={id}
                                type={'button'}
                            >
                                <div
                                    className="date-range-inputs"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}
                                >
                                    <TypableDateInput
                                        min={min}
                                        max={max}
                                        disabled={disabled}
                                        readOnly={readOnly}
                                        value={field?.value ? moment.utc(field?.value).toDate() : null}
                                        onChange={(date) => {
                                            if (date && date > new Date(2045, 11, 31)) return;
                                            const formattedDate = date ? moment.utc(date, 'YYYY-MM-DD').format('YYYY-MM-DD') : null;
                                            // Compare calendar days, not instants.
                                            const isWithinRange =
                                                !formattedDate || ((!min || formattedDate >= toDay(min)) && (!max || formattedDate <= toDay(max)));

                                            if (isWithinRange) {
                                                field.onChange(formattedDate);
                                                if (handleChange) {
                                                    handleChange(formattedDate);
                                                }
                                            }
                                        }}
                                    />
                                </div>
                                {!noShowIcons && (
                                    <div className="flex-align-center">
                                        {field?.value && !readOnly && (
                                            <button
                                                className="remove-calendar-day"
                                                type={'button'}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    field.onChange(null);
                                                    if (handleChange) {
                                                        handleChange(null);
                                                    }
                                                }}
                                            >
                                                <img src={Images.removeBlack} alt="remove" />
                                            </button>
                                        )}
                                        <img style={{ opacity: disabled ? 0.7 : 1 }} src={Images.calendarToday} alt="icon" />
                                    </div>
                                )}
                            </button>
                            {!noErrMessage && <ErrMessage text={error?.message} />}
                            <Popper
                                id={id}
                                open={open}
                                anchorEl={anchorEl}
                                className={'modal-custom-date-selector-box'}
                                placement="bottom-start"
                                style={{ position: 'absolute', zIndex: 1400 }}
                            >
                                <Calendar
                                    date={field?.value ? moment(field?.value).toDate() : min ? moment(min).toDate() : max ? moment(max).toDate() : new Date()}
                                    onChange={(date) => {
                                        const formattedDate = moment(date).format('YYYY-MM-DD');
                                        field.onChange(formattedDate);
                                        if (handleChange) {
                                            handleChange(formattedDate);
                                        }
                                    }}
                                    locale={customLocale}
                                    maxDate={max ? toLocalDate(max) : undefined}
                                    minDate={min ? toLocalDate(min) : undefined}
                                />
                                <button
                                    type={'button'}
                                    className="clear-bnt"
                                    onClick={() => {
                                        field.onChange(null);
                                        if (handleChange) {
                                            handleChange(null);
                                        }
                                    }}
                                >
                                    Clear
                                </button>
                            </Popper>
                            {open && <div className="beck-drop" onClick={() => setAnchorEl(null)} />}
                        </div>
                    </>
                );
            }}
        />
    );
};
